// NAS カーソル取り込み（v1.4.0）— 進捗パネル。
//
// 表示は「運用に必要な有界な情報」だけ。ファイル本文・抽出テキスト・絶対パス・
// 認証情報・内部サービスURL・任意の環境変数は**一切**扱わない
// （サーバー側の公開ビューにも含まれていない）。
//
// ここのボタンは助言的（advisory）である。実際の可否はサーバーが必ず再判定する。
// 活性・非活性はサーバーが返す can_pause / can_resume / can_cancel に従う。
import { Button } from '@/components/ui/button';
import { Play, Pause, StopCircle, ListMagnifyingGlass, ArrowsClockwise,
  Warning, CircleNotch, Info } from '@phosphor-icons/react';
import { deriveIngestState, shortId } from './nasJobState';
import { NasIngestRootCard } from './NasIngestRootCard';

const MAX_ROOT_CARDS = 50;

const n = (v) => Number(v || 0).toLocaleString('ja-JP');
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('ja-JP') : '—');

const SEARCHABLE = {
  lexical_and_semantic: ['本文検索 + 意味検索', 'Lexical + semantic', 'text-emerald-700'],
  lexical_only_embeddings_disabled: ['本文検索のみ（埋め込み無効）', 'Lexical only — embeddings off', 'text-zinc-600'],
  lexical_only_embeddings_pending: ['本文検索のみ（埋め込み未完了）', 'Lexical only — embeddings pending', 'text-amber-700'],
  lexical_only_embeddings_incompatible: ['本文検索のみ（埋め込み構成が不一致）', 'Lexical only — embedding config mismatch', 'text-rose-700'],
};

export function NasIngestProgressPanel({
  data, job, busy, action, onStart, onPause, onResume, onCancel, onRefresh,
  onOpenDetails, canStart, startBlockedReason,
}) {
  const flagOn = !!data?.cursor_ingestion_enabled;
  const sweepOn = !!data?.stale_reconciliation_enabled;
  const st = deriveIngestState(job);
  const Icon = st.Icon;
  const stats = job?.stats || {};
  const emb = job?.embedding || {};
  const q = job?.queues || {};
  const roots = job?.roots || [];
  const retries = roots.reduce((a, r) => a + Number(r.retries || 0), 0);
  const pageRetries = roots.reduce((a, r) => a + Number(r.page_retries || 0), 0);
  const [searchJa, searchEn, searchCls] =
    SEARCHABLE[job?.searchable_state] || ['—', '—', 'text-zinc-500'];

  return (
    <div className="rounded-lg border border-zinc-200 bg-white" data-testid="nas-ingest-panel">
      {/* ── 見出しと運用者フラグの状態 ─────────────────────────────── */}
      <div className="px-3 py-2 border-b border-zinc-200 flex flex-wrap items-center gap-2">
        <p className="text-[12px] font-semibold text-zinc-700">
          カーソル取り込み
          <span className="ml-1.5 text-[10.5px] font-normal text-zinc-500">
            Cursor ingestion (v1.4.0)
          </span>
        </p>
        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${
          flagOn ? 'bg-teal-50 border-teal-200 text-teal-800'
            : 'bg-zinc-100 border-zinc-300 text-zinc-600'}`}
          data-testid="nas-ingest-flag-state" data-enabled={String(flagOn)}>
          取り込みフラグ {flagOn ? 'ON' : 'OFF'}
        </span>
        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${
          sweepOn ? 'bg-amber-50 border-amber-300 text-amber-900'
            : 'bg-zinc-100 border-zinc-300 text-zinc-600'}`}
          data-testid="nas-ingest-sweep-state" data-enabled={String(sweepOn)}>
          削除照合 {sweepOn ? 'ON' : 'OFF'}
        </span>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={busy}
          className="h-7 text-[11px]" data-testid="nas-ingest-refresh">
          <ArrowsClockwise size={12} className="mr-1" /> 更新
        </Button>
      </div>

      {!flagOn && (
        <p className="mx-3 mt-2 rounded border border-zinc-200 bg-zinc-50 px-2.5 py-2 text-[11.5px] text-zinc-700"
          data-testid="nas-ingest-flag-off-notice">
          <Info size={12} weight="fill" className="inline mr-1 text-zinc-500" />
          カーソル取り込みは<b>無効</b>です（既定）。有効にするには
          「設定 → エビデンスルーター → 機能フラグ」で
          <span className="font-mono mx-1">NASカーソル取り込み</span>を ON にしてください。
          <span className="block text-[10.5px] text-zinc-500 mt-0.5">
            Cursor ingestion is disabled by default. An administrator must enable the
            operator flag before it can run.
          </span>
        </p>
      )}

      {/* ── 状態 ───────────────────────────────────────────────────── */}
      <div className="px-3 py-2.5 space-y-2.5">
        {!job ? (
          <p className="text-[12px] text-zinc-400 rounded border border-dashed border-zinc-200 px-3 py-2.5"
            data-testid="nas-ingest-empty">
            カーソル取り込みはまだ実行されていません。
            <span className="block text-[11px]">No cursor ingestion has been run yet.</span>
          </p>
        ) : (
          <>
            <div className={`rounded-lg border px-3 py-2 ${st.border} ${st.bg}`}
              data-testid="nas-ingest-status-card" data-status={job.status}
              data-state-key={st.key}>
              <div className="flex items-center gap-2">
                <Icon size={15} weight="fill"
                  className={`${st.color} shrink-0 ${st.spin ? 'animate-spin' : ''}`} />
                <span className={`text-[12.5px] font-bold ${st.color}`}
                  data-testid="nas-ingest-status-label">{st.labelJa}</span>
                <span className="text-[10.5px] text-zinc-500">{st.labelEn}</span>
                <div className="flex-1" />
                <span className="text-[10px] font-mono text-zinc-400"
                  data-testid="nas-ingest-job-id">{shortId(job.id)}</span>
              </div>
              {job.progress_label && (
                <p className="mt-1 text-[11.5px] text-zinc-700" data-testid="nas-ingest-progress-label">
                  {job.progress_label}
                </p>
              )}
              <div className="mt-1.5 h-1.5 rounded-full bg-white/70 overflow-hidden">
                <div className="h-full bg-teal-600 transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.round(
                    (Number(job.roots_complete || 0) / Math.max(1, Number(job.total_roots || 1))) * 100))}%` }}
                  data-testid="nas-ingest-progress-bar" />
              </div>
              <p className="mt-1 text-[10.5px] text-zinc-600 tabular-nums"
                data-testid="nas-ingest-progress-summary">
                ルート {n(job.roots_complete)} / {n(job.total_roots)} 完了
                {' · '}現在のルート <b className="font-mono">{job.current_root || '—'}</b>
                {' · '}現在のページ {n(job.current_page)}
                {' · '}巡回 {n(job.rounds)} 周
              </p>
            </div>

            {/* ── 主要カウンター（必須表示項目） ────────────────────── */}
            <dl className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-x-2 gap-y-1.5 text-[11px]"
              data-testid="nas-ingest-counters">
              {[
                ['検出', 'Observed', stats.files_observed, ''],
                ['対象', 'Eligible', stats.eligible_files, ''],
                ['処理済み', 'Processed', stats.processed_files, ''],
                ['登録', 'Indexed', stats.indexed, 'text-emerald-700'],
                ['変更なし', 'Unchanged', stats.unchanged, ''],
                ['対象外', 'Skipped', stats.skipped, ''],
                ['エラー', 'Failed', stats.failed,
                  Number(stats.failed) > 0 ? 'text-rose-700' : ''],
              ].map(([ja, en, val, cls]) => (
                <div key={en} className="min-w-0">
                  <dt className="text-zinc-500 truncate">{ja}
                    <span className="block text-[9.5px] text-zinc-400">{en}</span>
                  </dt>
                  <dd className={`font-bold tabular-nums ${cls || 'text-zinc-800'}`}
                    data-testid={`nas-ingest-stat-${en.toLowerCase()}`}>{n(val)}</dd>
                </div>
              ))}
            </dl>

            {/* ── 埋め込みと待ち行列 ───────────────────────────────── */}
            <div className="rounded border border-zinc-200 bg-zinc-50 px-2.5 py-2"
              data-testid="nas-ingest-embedding">
              <p className="text-[10.5px] font-semibold text-zinc-600">
                埋め込み / Embeddings
                <span className="ml-1.5 font-normal text-zinc-500">
                  {emb.enabled ? '有効 / on' : '停止中 / off'}
                  {emb.dimensions ? ` · ${emb.dimensions}次元` : ''}
                </span>
              </p>
              <dl className="mt-1 grid grid-cols-3 sm:grid-cols-6 gap-x-2 gap-y-1 text-[11px]">
                {[
                  ['待ち', 'Pending', emb.pending, ''],
                  ['処理中', 'Processing', emb.processing, ''],
                  ['完了', 'Completed', emb.done, 'text-emerald-700'],
                  ['失敗', 'Failed', emb.failed,
                    Number(emb.failed) > 0 ? 'text-rose-700' : ''],
                  ['待ち行列', 'Queue depth', emb.queue_depth ?? q.embed_inflight, ''],
                  ['最高水位', 'High-water', emb.queue_high_water, ''],
                ].map(([ja, en, val, cls]) => (
                  <div key={en} className="min-w-0">
                    <dt className="text-zinc-500 truncate">{ja}
                      <span className="block text-[9.5px] text-zinc-400">{en}</span>
                    </dt>
                    <dd className={`font-semibold tabular-nums ${cls || 'text-zinc-800'}`}
                      data-testid={`nas-ingest-embed-${en.toLowerCase().replace(/[ -]/g, '')}`}>
                      {n(val)}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className={`mt-1 text-[10.5px] font-semibold ${searchCls}`}
                data-testid="nas-ingest-searchable-state" data-value={job.searchable_state || ''}>
                検索可能な状態: {searchJa}
                <span className="ml-1 font-normal text-zinc-500">/ {searchEn}</span>
              </p>
              {emb.toggle_enabled && emb.compatible === false && (
                <p className="mt-1 text-[10.5px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1"
                  data-testid="nas-ingest-embed-incompatible">
                  <Warning size={11} weight="fill" className="inline mr-1" />
                  埋め込みの構成が想定と一致しないため、ベクトルは 1 件も書き込んでいません
                  （本文検索はそのまま使えます）。分類:{' '}
                  <span className="font-mono">{emb.compat_reason}</span>
                </p>
              )}
            </div>

            {/* ── 実行の健全性 ─────────────────────────────────────── */}
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-1 text-[11px]"
              data-testid="nas-ingest-health">
              {[
                ['再試行', 'Retries', n(retries)],
                ['ページ再試行', 'Page retries', n(pageRetries)],
                ['NASページ取得', 'Pages fetched', n(job.nas_page_requests)],
                ['最終応答', 'Last heartbeat', fmtDT(job.heartbeat_at)],
              ].map(([ja, en, val]) => (
                <div key={en} className="min-w-0">
                  <dt className="text-zinc-500 truncate">{ja}
                    <span className="block text-[9.5px] text-zinc-400">{en}</span>
                  </dt>
                  <dd className="font-semibold text-zinc-800 tabular-nums truncate"
                    title={String(val)}
                    data-testid={`nas-ingest-health-${en.toLowerCase().replace(/ /g, '-')}`}>
                    {val}
                  </dd>
                </div>
              ))}
            </dl>

            {/* ── 打ち切り・整合性の理由 ───────────────────────────── */}
            {(job.truncated || job.typed_error
              || job.consistency === 'changed_during_enumeration') && (
              <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-2"
                data-testid="nas-ingest-truncation">
                <Warning size={12} weight="fill" className="inline mr-1" />
                {job.truncation_message_ja
                  || (job.truncated ? 'この実行は上限に達したため不完全です。'
                    : 'この実行は完全ではありません。')}
                <span className="block text-[10.5px] opacity-80">
                  {(job.truncation_reasons || []).length > 1
                    ? `他 ${job.truncation_reasons.length - 1} 件の理由あり` : ''}
                </span>
                <span className="block text-[10px] font-mono opacity-70 mt-0.5">
                  {(job.truncation_reasons || []).join(', ') || job.typed_error || '—'}
                  {job.consistency ? ` · consistency=${job.consistency}` : ''}
                </span>
              </p>
            )}

            {job.stale && (
              <p className="text-[11px] text-zinc-700 bg-zinc-50 border border-zinc-200 rounded px-2.5 py-2"
                data-testid="nas-ingest-stale-summary">
                削除照合 / Stale reconciliation:{' '}
                候補 <b className="tabular-nums">{n(job.stale.candidates)}</b> 件
                {' · '}削除{' '}
                <b className={`tabular-nums ${Number(job.stale.deleted) > 0 ? 'text-rose-700' : ''}`}>
                  {n(job.stale.deleted)}
                </b> 件
                <span className="block text-[10px] font-mono opacity-70">
                  {job.stale.reason || '—'}
                </span>
              </p>
            )}

            {/* ── ルートごとの進捗（既定は閉じたカード） ───────────── */}
            <div data-testid="nas-ingest-roots">
              <p className="text-[10.5px] font-semibold text-zinc-500 mb-1">
                ルートごとの進捗 / Progress by root
                <span className="ml-1 font-normal text-zinc-400">
                  （カードを押すと内訳が開きます）
                </span>
              </p>
              <div className="space-y-1.5">
                {roots.slice(0, MAX_ROOT_CARDS).map((r) => (
                  <NasIngestRootCard key={r.path} root={r}
                    isCurrent={r.path === job.current_root} />
                ))}
              </div>
              {job.roots_truncated_for_display && (
                <p className="mt-1 text-[10.5px] text-amber-700"
                  data-testid="nas-ingest-roots-truncated">
                  ルートが多いため先頭 {MAX_ROOT_CARDS} 件のみ表示しています
                  （全 {n(job.total_roots)} 件）。
                </p>
              )}
            </div>
          </>
        )}

        {/* ── 操作（サーバーが許す場合のみ活性） ─────────────────── */}
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <Button onClick={onStart} disabled={busy || !canStart}
            className="h-8 bg-teal-700 hover:bg-teal-800 text-[12px]"
            data-testid="nas-ingest-start-btn">
            {action === 'start' ? <CircleNotch size={12} className="mr-1 animate-spin" />
              : <Play size={12} weight="fill" className="mr-1" />}
            取り込み開始 / Start
          </Button>
          {job?.can_pause && (
            <Button onClick={onPause} disabled={busy} variant="outline"
              className="h-8 text-[12px]" data-testid="nas-ingest-pause-btn">
              <Pause size={12} weight="fill" className="mr-1" /> 一時停止 / Pause
            </Button>
          )}
          {job?.can_resume && (
            <Button onClick={onResume} disabled={busy || !flagOn}
              className="h-8 bg-sky-700 hover:bg-sky-800 text-[12px]"
              data-testid="nas-ingest-resume-btn">
              <Play size={12} weight="fill" className="mr-1" /> 再開 / Resume
            </Button>
          )}
          {job?.can_cancel && (
            <Button onClick={onCancel} disabled={busy} variant="outline"
              className="h-8 text-[12px] border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="nas-ingest-cancel-btn">
              <StopCircle size={12} className="mr-1" /> 中止 / Cancel
            </Button>
          )}
          {job && (
            <Button onClick={onOpenDetails} variant="outline" className="h-8 text-[12px]"
              data-testid="nas-ingest-details-btn">
              <ListMagnifyingGlass size={12} className="mr-1" /> 明細を見る / View details
            </Button>
          )}
        </div>

        {job?.can_resume && !flagOn && (
          <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5"
            data-testid="nas-ingest-resume-blocked">
            再開するには、先に「NASカーソル取り込み」フラグを ON にしてください。
            <span className="block text-[10.5px] opacity-80">
              Enable the operator flag before resuming.
            </span>
          </p>
        )}

        {!canStart && startBlockedReason && (
          <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5"
            data-testid="nas-ingest-start-blocked">
            <Warning size={12} weight="fill" className="inline mr-1" />
            {startBlockedReason}
          </p>
        )}

        <p className="text-[10px] text-zinc-400" data-testid="nas-ingest-advisory-note">
          ここの操作は助言的な表示です。実行の可否はサーバー側で必ず再判定されます。
          <span className="ml-1">/ Controls are advisory; the server re-checks every action.</span>
        </p>
      </div>
    </div>
  );
}

export default NasIngestProgressPanel;
